import { useState } from "react";
import Swal from "sweetalert2";
import API_CIFRA from "../config/Variables_apis";
import { tokenManager } from "../config/authConfig";
import useCategorias from "./useCategorias";
import useIncidencias from "./useIncidencias";
import useArchivos from "./useArchivos";

const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Usar los hooks específicos
  const categoriasHook = useCategorias();
  const incidenciasHook = useIncidencias();
  const archivosHook = useArchivos();

  // Función para obtener token automáticamente (si existe)
  const getAuthHeaders = () => {
    const token = tokenManager.getAccessToken();
    const headers = {
      "Content-Type": "application/json",
    };
    
    // Si hay token válido, agregarlo
    if (token && !tokenManager.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Función para manejar errores específicos
  const handleApiError = (error, response) => {
    let errorMessage = "Error al enviar los datos";
    
    if (response?.status === 400) {
      errorMessage = "Datos inválidos. Verifica la información ingresada.";
    } else if (response?.status === 401) {
      errorMessage = "Error de autenticación. Intenta nuevamente.";
    } else if (response?.status === 403) {
      errorMessage = "No tienes permisos para realizar esta acción.";
    } else if (response?.status === 404) {
      errorMessage = "Recurso no encontrado.";
    } else if (response?.status === 500) {
      errorMessage = "Error interno del servidor. Intenta más tarde.";
    }
    
    return errorMessage;
  };

  const enviarDatos = async (data) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Crear la incidencia usando el hook específico
      const incidenciaResult = await incidenciasHook.crearIncidencia(data);
      
      if (!incidenciaResult.success) {
        throw new Error(incidenciaResult.error);
      }

      // 2. Subir todos los archivos capturados/seleccionados
      if (incidenciaResult.incidenciaId) {
        console.log('📤 Iniciando subida de archivos...');
        console.log('📤 ID de incidencia:', incidenciaResult.incidenciaId);
        console.log('📤 Datos recibidos:', data);
        
        const uploads = [];
        
        // Compatibilidad: foto simple
        if (data.foto) {
          console.log('📤 Subiendo foto simple:', data.foto);
          uploads.push(archivosHook.subirArchivo(
            data.foto,
            incidenciaResult.incidenciaId,
            data.latitud,
            data.longitud
          ));
        }
        
        // Nuevo: mediaFiles con múltiples tipos
        if (data.mediaFiles) {
          console.log('📤 MediaFiles encontrados:', data.mediaFiles);
          const { photos = [], videos = [], audio = [], files = [] } = data.mediaFiles;
          const allFiles = [...photos, ...videos, ...audio, ...files];
          console.log('📤 Total de archivos a subir:', allFiles.length);
          
          for (const item of allFiles) {
            console.log('📤 Procesando item:', item);
            const file = item.file;
            console.log('📤 File extraído:', file);
            console.log('📤 Tipo de file:', typeof file);
            console.log('📤 Es File?', file instanceof File);
            console.log('📤 Es Blob?', file instanceof Blob);
            
            if (!file) {
              console.error('❌ Archivo no encontrado en item:', item);
              continue;
            }
            
            if (!(file instanceof File) && !(file instanceof Blob)) {
              console.error('❌ El archivo no es un File o Blob válido:', file);
              continue;
            }
            
            console.log('📤 Subiendo archivo:', file.name, file.type, file.size);
            uploads.push(archivosHook.subirArchivo(
              file,
              incidenciaResult.incidenciaId,
              data.latitud,
              data.longitud
            ));
          }
        } else {
          console.log('📤 No se encontraron mediaFiles en los datos');
        }
        
        if (uploads.length > 0) {
          console.log('📤 Iniciando subida de', uploads.length, 'archivos...');
          const results = await Promise.allSettled(uploads);
          const failed = results.filter(r => r.status === 'rejected' || (r.value && !r.value.success));
          if (failed.length) {
            console.warn(`❌ Incidencia creada pero ${failed.length} archivos fallaron en subir.`);
            console.warn('❌ Errores:', failed);
          } else {
            console.log('✅ Todos los archivos subidos exitosamente');
          }
        } else {
          console.log('📤 No hay archivos para subir');
        }
      }

      // 3. Mostrar mensaje de éxito con ticket
      // El mensaje de éxito se maneja en EnvioAlerta.jsx (showSuccessWithTicket)

      return { 
        success: true, 
        data: incidenciaResult.data,
        ticket: incidenciaResult.ticket,
        incidenciaId: incidenciaResult.incidenciaId
      };

    } catch (error) {
      const errorMessage = error.message || "Ocurrió un error al enviar la alerta";
      setError(errorMessage);
      
      await Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "Entendido"
      });

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para buscar incidencia por ticket
  const buscarIncidencia = async (ticket) => {
    return await incidenciasHook.buscarIncidenciaPorTicket(ticket);
  };

  // Función para obtener categorías
  const obtenerCategorias = async () => {
    return await categoriasHook.cargarCategorias();
  };

  return { 
    // Funciones principales
    enviarDatos, 
    buscarIncidencia, 
    obtenerCategorias,
    
    // Estados
    loading: loading || categoriasHook.loading || incidenciasHook.loading || archivosHook.loading, 
    error: error || categoriasHook.error || incidenciasHook.error || archivosHook.error,
    
    // Hooks específicos para acceso directo
    categorias: categoriasHook,
    incidencias: incidenciasHook,
    archivos: archivosHook,
  };
};

export default useApi;